/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import { CancellationToken } from "@gnu-taler/taler-util";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";

export interface AsyncActionOptions<Result, Args extends unknown[]> {
  onResult?: (result: Result, ...args: Args) => void | Promise<void>;
  onError?: (error: unknown, ...args: Args) => void | Promise<void>;
}

export interface AsyncAction<Args extends unknown[]> {
  running: boolean;
  run: (...args: Args) => Promise<void>;
  cancel: () => void;
}

/**
 * Run one cancellable asynchronous operation at a time.
 *
 * The operation and callbacks always come from the latest render.  Completion
 * after unmount is ignored, and cancellation is not reported as a failure.
 */
export function useAsyncAction<Result, Args extends unknown[]>(
  operation: (token: CancellationToken, ...args: Args) => Promise<Result>,
  options: AsyncActionOptions<Result, Args> = {},
): AsyncAction<Args> {
  const [running, setRunning] = useState(false);
  const runningRef = useRef(false);
  const mounted = useRef(true);
  const source = useRef<CancellationToken.Source>();
  const operationRef = useRef(operation);
  const optionsRef = useRef(options);
  operationRef.current = operation;
  optionsRef.current = options;

  const cancel = useCallback(() => {
    source.current?.cancel();
  }, []);

  useEffect(
    () => () => {
      mounted.current = false;
      source.current?.cancel();
      source.current?.dispose();
    },
    [],
  );

  const run = useCallback(async (...args: Args): Promise<void> => {
    if (runningRef.current) return;
    runningRef.current = true;
    setRunning(true);
    const currentSource = CancellationToken.create();
    source.current = currentSource;
    try {
      const result = await currentSource.token.racePromise(
        operationRef.current(currentSource.token, ...args),
      );
      if (mounted.current) {
        await optionsRef.current.onResult?.(result, ...args);
      }
    } catch (error: unknown) {
      if (
        mounted.current &&
        !(error instanceof CancellationToken.CancellationError)
      ) {
        await optionsRef.current.onError?.(error, ...args);
      }
    } finally {
      currentSource.dispose();
      if (source.current === currentSource) source.current = undefined;
      runningRef.current = false;
      if (mounted.current) setRunning(false);
    }
  }, []);

  return { running, run, cancel };
}
